import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil, Settings } from "lucide-react";
import Modal from "./Modal";
import Field, { inputStyle, selectStyle } from "./Field";
import { FORMAS_PAGAMENTO, TABELA_PADRAO, BRAZILIAN_STATES, problemasDaTabela, quadroDaTabela, copiaDaTabela, novoCodigoExclusivo, ehExclusiva } from "../constants";
import { listarTabelas, salvarTabela, listarRegioes, marcarRegiao, ufsCondicaoExclusiva } from "../lib/tabelasComerciais";

// Tabelas comerciais do combustível: o comercial cria uma por região ou por
// fornecedor (taxa por prazo, prazos, aluguel/isenção da maquininha e forma de
// pagamento) e escolhe uma por ficha. Mudar uma tabela aqui NÃO muda ficha
// nenhuma: cada ficha guarda a cópia que o posto aceitou (R-127).

const VAZIA = { codigo: "", nome: "", descricao: "", prazos: [{ dias: 7, taxa: "" }, { dias: 15, taxa: "" }, { dias: 30, taxa: "" }], aluguel_mensal: "4,99", isencao_acima: "10000", formas_pagamento: ["pix"], ativa: true };
const btn = { display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 12px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" };
const btnPrim = { ...btn, border: "none", background: "#7c3aed", color: "#fff" };
const virgula = (v) => (v === null || v === undefined ? "" : String(v).replace(".", ","));

// A cópia que está na ficha (camelCase) volta para o formato do editor.
const doSnapshot = (t) => ({
  codigo: t.codigo, nome: t.nome, exclusiva: true,
  prazos: (t.prazos || []).map((p) => ({ dias: p.dias, taxa: p.taxa })),
  aluguel_mensal: t.aluguelMensal ?? 0, isencao_acima: t.isencaoAcima ?? "",
  formas_pagamento: t.formasPagamento || [],
});

// `exclusiva`: condição só deste fornecedor — não grava em tabelas_comerciais;
// devolve a linha para a ficha/convite guardar.
function Editor({ inicial, onSalvo, onCancelar, exclusiva = false }) {
  const [t, setT] = useState(() => ({
    ...inicial,
    prazos: (inicial.prazos || []).map((p) => ({ dias: p.dias, taxa: virgula(p.taxa) })),
    aluguel_mensal: virgula(inicial.aluguel_mensal),
    isencao_acima: virgula(inicial.isencao_acima),
  }));
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const set = (patch) => setT((x) => ({ ...x, ...patch }));
  const setPrazo = (i, patch) => set({ prazos: t.prazos.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const problemas = problemasDaTabela({ ...t, codigo: String(t.codigo || "").trim().toUpperCase() });

  const salvar = async () => {
    if (problemas.length) { setErro(`Falta ou está errado: ${problemas.join("; ")}`); return; }
    setSalvando(true); setErro("");
    try {
      onSalvo(exclusiva
        ? { ...t, codigo: String(t.codigo).trim().toUpperCase(), exclusiva: true, atualizado_em: new Date().toISOString() }
        : await salvarTabela(t));
    } catch (e) { setErro(e.message); } finally { setSalvando(false); }
  };

  return (
    <div>
      {exclusiva && (
        <div style={{ fontSize: 12, color: "#5b21b6", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 8, padding: "8px 10px", marginBottom: 12, lineHeight: 1.45 }}>
          Condição <strong>exclusiva deste fornecedor</strong>: não entra na lista de tabelas e nenhum outro posto vê.
          O posto recebe estes números no quadro e aceita junto com o termo.
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "0 10px" }}>
        <Field label="Código" required help="Curto e sem espaço. Ex: GO-INTERIOR, REDE-FLEX. Vai no PDF e no sistema de combustível.">
          <input style={inputStyle} value={t.codigo} disabled={!!t.id || exclusiva} onChange={(e) => set({ codigo: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "") })} placeholder="GO-INTERIOR" />
        </Field>
        <Field label="Nome" required help="O que o posto lê no quadro de condições.">
          <input style={inputStyle} value={t.nome} onChange={(e) => set({ nome: e.target.value })} placeholder="Goiás — interior" />
        </Field>
      </div>
      <Field label="Para quem é (interno)" help="Região, rede ou fornecedor. Só a equipe vê.">
        <input style={inputStyle} value={t.descricao || ""} onChange={(e) => set({ descricao: e.target.value })} placeholder="Postos do interior de GO; rede Flex…" />
      </Field>

      <Field label="Prazos de repasse e taxa" required help="Cada linha é um prazo que o posto pode escolher. 7, 15 e 30 dias aparecem como semanal, quinzenal e mensal.">
        {t.prazos.map((p, i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
            <input style={{ ...inputStyle, width: 90 }} value={p.dias} inputMode="numeric" onChange={(e) => setPrazo(i, { dias: e.target.value.replace(/\D/g, "") === "" ? "" : Number(e.target.value.replace(/\D/g, "")) })} />
            <span style={{ fontSize: 12, color: "#64748b" }}>dias →</span>
            <input style={{ ...inputStyle, width: 90 }} value={p.taxa} onChange={(e) => setPrazo(i, { taxa: e.target.value.replace(/[^0-9,.]/g, "") })} placeholder="2,5" />
            <span style={{ fontSize: 12, color: "#64748b" }}>% do faturado</span>
            <button type="button" onClick={() => set({ prazos: t.prazos.filter((_, j) => j !== i) })} style={{ ...btn, padding: "6px 8px" }} title="Tirar este prazo"><Trash2 size={13} /></button>
          </div>
        ))}
        <button type="button" onClick={() => set({ prazos: [...t.prazos, { dias: "", taxa: "" }] })} style={btn}><Plus size={13} /> Prazo</button>
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 10px" }}>
        <Field label="Aluguel da maquininha (R$/mês)" required help="Por equipamento. 0 = sem aluguel.">
          <input style={inputStyle} value={t.aluguel_mensal} onChange={(e) => set({ aluguel_mensal: e.target.value.replace(/[^0-9,.]/g, "") })} />
        </Field>
        <Field label="Isento acima de (R$ faturados no mês)" help="Em branco = sem isenção automática.">
          <input style={inputStyle} value={t.isencao_acima} onChange={(e) => set({ isencao_acima: e.target.value.replace(/[^0-9,.]/g, "") })} />
        </Field>
      </div>

      <Field label="Formas de pagamento do repasse" required help="Com mais de uma, o posto escolhe no formulário. Sempre em conta/chave do mesmo CNPJ.">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {FORMAS_PAGAMENTO.map((f) => {
            const on = t.formas_pagamento.includes(f.id);
            return (
              <button key={f.id} type="button" onClick={() => set({ formas_pagamento: on ? t.formas_pagamento.filter((x) => x !== f.id) : [...t.formas_pagamento, f.id] })}
                style={{ ...btn, border: on ? "1.5px solid #7c3aed" : "1px solid #e2e8f0", background: on ? "#f5f3ff" : "#fff", color: on ? "#6d28d9" : "#475569" }}>
                {f.label}
              </button>
            );
          })}
        </div>
      </Field>

      {t.id && (
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: "#334155", marginBottom: 12 }}>
          <input type="checkbox" checked={t.ativa !== false} onChange={(e) => set({ ativa: e.target.checked })} /> Ativa (desativada some da escolha; fichas que já usam continuam iguais)
        </label>
      )}

      {!problemas.length && (
        <div style={{ fontSize: 11.5, color: "#1e293b", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "8px 10px", marginBottom: 12, lineHeight: 1.5 }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>O posto vai ver:</div>
          {quadroDaTabela(copiaDaTabela({ ...t, aluguel_mensal: t.aluguel_mensal, isencao_acima: t.isencao_acima })).map((l, i) => <div key={i}>{l}</div>)}
        </div>
      )}
      {erro && <div style={{ fontSize: 12, color: "#b91c1c", marginBottom: 10 }}>{erro}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" onClick={onCancelar} style={btn}>Cancelar</button>
        <button type="button" onClick={salvar} disabled={salvando} style={btnPrim}>{salvando ? "Salvando…" : exclusiva ? "Usar esta condição" : "Salvar tabela"}</button>
      </div>
    </div>
  );
}

/** Modal de gestão das tabelas. */
export default function TabelasComerciais({ open, onClose, onMudou }) {
  const [tabelas, setTabelas] = useState([]);
  const [editando, setEditando] = useState(null);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    try { setTabelas(await listarTabelas()); setErro(""); } catch (e) { setErro(e.message); }
  }, []);
  useEffect(() => { if (open) { setEditando(null); carregar(); } }, [open, carregar]);

  return (
    <Modal open={open} onClose={onClose} title="Tabelas comerciais — combustível" w={680}>
      {editando ? (
        <Editor inicial={editando} onCancelar={() => setEditando(null)} onSalvo={() => { setEditando(null); carregar(); onMudou?.(); }} />
      ) : (
        <div>
          <div style={{ fontSize: 12.5, color: "#64748b", marginBottom: 12, lineHeight: 1.5 }}>
            Cada posto aceita <strong>uma</strong> tabela, escolhida na ficha ou ao enviar o link. Alterar uma tabela vale
            só para fichas novas — quem já aceitou continua com o que aceitou.
          </div>
          <div style={{ padding: "9px 11px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#f8fafc", marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>{TABELA_PADRAO.nome} <span style={{ fontSize: 11, color: "#94a3b8" }}>PADRAO · fixa</span></div>
            <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 3 }}>{quadroDaTabela(TABELA_PADRAO).slice(1).join(" · ")}</div>
          </div>
          {tabelas.map((t) => (
            <div key={t.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "9px 11px", borderRadius: 9, border: "1px solid #e2e8f0", marginBottom: 8, opacity: t.ativa ? 1 : 0.55 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>{t.nome} <span style={{ fontSize: 11, color: "#94a3b8" }}>{t.codigo}{t.ativa ? "" : " · desativada"}</span></div>
                {t.descricao && <div style={{ fontSize: 11.5, color: "#7c3aed", marginTop: 2 }}>{t.descricao}</div>}
                <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 3 }}>{quadroDaTabela(copiaDaTabela(t)).slice(1).join(" · ")}</div>
              </div>
              <button type="button" onClick={() => setEditando(t)} style={btn}><Pencil size={13} /> Editar</button>
            </div>
          ))}
          {erro && <div style={{ fontSize: 12, color: "#b91c1c", margin: "8px 0" }}>Não foi possível carregar: {erro}</div>}
          <button type="button" onClick={() => setEditando({ ...VAZIA })} style={{ ...btnPrim, marginTop: 6 }}><Plus size={14} /> Nova tabela</button>
          <Regioes />
        </div>
      )}
    </Modal>
  );
}

/** Estados em que a taxa é definida no cadastro, exclusiva de cada posto. */
function Regioes() {
  const [ufs, setUfs] = useState([]);
  const [erro, setErro] = useState("");
  const carregar = useCallback(() => {
    listarRegioes().then((r) => setUfs(r.filter((x) => x.exige_condicao_exclusiva).map((x) => x.uf))).catch((e) => setErro(e.message));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);
  const alternar = async (uf) => {
    try { await marcarRegiao(uf, !ufs.includes(uf)); setErro(""); carregar(); } catch (e) { setErro(e.message); }
  };
  return (
    <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #ede9fe" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>Estados com taxa definida no cadastro</div>
      <div style={{ fontSize: 11.5, color: "#64748b", margin: "3px 0 10px", lineHeight: 1.45 }}>
        Nos estados marcados não vale a tabela padrão: cada posto recebe uma <strong>condição exclusiva</strong>, criada
        no envio do link ou na ficha. Sem ela, o posto envia a ficha com "condições a definir" e o banco não aplica taxa.
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
        {BRAZILIAN_STATES.map(({ uf }) => {
          const on = ufs.includes(uf);
          return (
            <button key={uf} type="button" onClick={() => alternar(uf)}
              style={{ ...btn, padding: "5px 9px", border: on ? "1.5px solid #7c3aed" : "1px solid #e2e8f0", background: on ? "#f5f3ff" : "#fff", color: on ? "#6d28d9" : "#94a3b8" }}>
              {uf}
            </button>
          );
        })}
      </div>
      {erro && <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 6 }}>{erro}</div>}
    </div>
  );
}

/**
 * Seletor da condição comercial de uma ficha: tabela padrão, uma tabela
 * compartilhada ou uma condição EXCLUSIVA deste fornecedor.
 * `atual` = cópia que já está na ficha (para mostrar e editar a exclusiva).
 * `onEscolher(linha | null)` recebe a linha (null = padrão).
 * `estado`: UF do posto — nos estados de taxa definida no cadastro a padrão some.
 */
export function EscolherTabela({ value = "", atual = null, onEscolher, disabled = false, aviso = "", estado = "", nomePosto = "" }) {
  const [tabelas, setTabelas] = useState([]);
  const [ufs, setUfs] = useState([]);
  const [gerir, setGerir] = useState(false);
  const [exclusiva, setExclusiva] = useState(null);
  const carregar = useCallback(() => { listarTabelas({ soAtivas: true }).then(setTabelas).catch(() => setTabelas([])); }, []);
  useEffect(() => { carregar(); ufsCondicaoExclusiva().then(setUfs); }, [carregar]);
  const uf = String(estado || "").trim().toUpperCase();
  const exige = !!uf && ufs.includes(uf);
  const atualExclusiva = ehExclusiva(atual) ? atual : null;
  const temValor = !value || !!atualExclusiva || tabelas.some((t) => t.codigo === value);
  const abrirExclusiva = () => setExclusiva(atualExclusiva ? doSnapshot(atualExclusiva) : {
    ...VAZIA, codigo: novoCodigoExclusivo(), nome: `Condição exclusiva${nomePosto ? ` — ${nomePosto}` : ""}`, exclusiva: true,
  });
  const avisoExige = exige && !value ? `Posto em ${uf}: a taxa é definida no cadastro. Crie a condição exclusiva deste fornecedor.` : "";
  return (
    <Field label="Condição comercial" help="Os números que este posto vai ver e aceitar: taxa por prazo, aluguel da maquininha e forma de pagamento.">
      <div style={{ display: "flex", gap: 6 }}>
        <select
          style={{ ...selectStyle, ...(avisoExige ? { border: "1px solid #f59e0b" } : {}) }}
          value={value}
          disabled={disabled}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "__exclusiva__") { abrirExclusiva(); return; }
            if (atualExclusiva && v === atualExclusiva.codigo) return;
            onEscolher(v ? tabelas.find((t) => t.codigo === v) || null : null);
          }}
        >
          <option value="">{exige ? `— Defina a condição (${uf} não usa a padrão) —` : TABELA_PADRAO.nome}</option>
          {atualExclusiva && <option value={atualExclusiva.codigo}>★ {atualExclusiva.nome} (exclusiva)</option>}
          {!temValor && <option value={value}>{value} (desativada)</option>}
          {tabelas.map((t) => <option key={t.id} value={t.codigo}>{t.nome} — {t.codigo}</option>)}
          <option value="__exclusiva__">{atualExclusiva ? "★ Editar a condição exclusiva…" : "★ Condição exclusiva deste fornecedor…"}</option>
        </select>
        <button type="button" onClick={() => setGerir(true)} style={btn} title="Criar ou editar tabelas e estados"><Settings size={13} /></button>
      </div>
      {(avisoExige || aviso) && <div style={{ fontSize: 11, color: "#b45309", marginTop: 5 }}>{avisoExige || aviso}</div>}
      <TabelasComerciais open={gerir} onClose={() => setGerir(false)} onMudou={() => { carregar(); ufsCondicaoExclusiva({ recarregar: true }).then(setUfs); }} />
      <Modal open={!!exclusiva} onClose={() => setExclusiva(null)} title="Condição exclusiva deste fornecedor" w={640}>
        {exclusiva && (
          <Editor exclusiva inicial={exclusiva} onCancelar={() => setExclusiva(null)} onSalvo={(linha) => { setExclusiva(null); onEscolher(linha); }} />
        )}
      </Modal>
    </Field>
  );
}
